<template>
  <div class="page" :class="{ embedded: embedded }">
    <div v-if="isSaving" class="saving-overlay">
      <div class="saving-card">Salvando dados...</div>
    </div>
    <div class="card" style="max-width: 800px;">
      <div class="card-header">
        <div>
          <h1 class="title">Dialogos de Seguranca</h1>
          <p class="subtitle">Registro do dialogo com a equipe.</p>
        </div>
        <span class="pill">Seguranca</span>
      </div>

      <form class="form-grid" @submit.prevent>
        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
          <div class="input-field">
            <label for="attraction">Projeto</label>
            <select id="attraction" v-model="attraction">
              <option v-for="item in projectOptions" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
          <div class="input-field">
            <label for="theme">Tema do dialogo</label>
            <input id="theme" v-model="theme" type="text" placeholder="Tema principal" />
          </div>
          <div class="input-field">
            <label for="reportDate">Data</label>
            <input id="reportDate" v-model="reportDate" type="date" />
          </div>
        </div>

        <section class="section">
          <div class="section-header">
            <h2 class="section-title">Participantes</h2>
            <button class="btn btn-ghost" type="button" @click="addSelectedParticipant">Adicionar selecionado</button>
          </div>
          <div class="grid" style="gap: 10px;">
            <select v-model="selectedParticipant">
              <option value="">Selecione um subordinado/colaborador</option>
              <option v-for="option in participantOptions" :key="option.username" :value="option.username">
                {{ option.name }} ({{ option.username }})
              </option>
            </select>
          </div>
          <div v-if="participants.length === 0" class="hint">Nenhum participante adicionado.</div>
          <div v-else class="grid" style="gap: 10px;">
            <div v-for="(item, index) in participants" :key="`p-${index}`" class="list-row">
              <div>{{ participantLabel(item) }}</div>
              <button class="btn btn-accent" type="button" @click="removeParticipant(index)">Excluir</button>
            </div>
          </div>
          <button class="btn btn-ghost" type="button" @click="showLegacyMethod = !showLegacyMethod">
            {{ showLegacyMethod ? 'Ocultar metodo de texto' : 'Usar metodo alternativo (texto)' }}
          </button>
          <div v-if="showLegacyMethod" class="grid" style="gap: 10px;">
            <div v-for="(item, index) in legacyParticipants" :key="`legacy-${index}`" class="list-row">
              <input v-model="legacyParticipants[index]" type="text" placeholder="Nome escrito manualmente" />
              <button class="btn btn-accent" type="button" @click="removeLegacyParticipant(index)">Excluir</button>
            </div>
            <button class="btn btn-ghost" type="button" @click="addLegacyParticipant">Adicionar nome manual</button>
          </div>
        </section>

        <p v-if="error" class="subtitle" style="color: #a54b2a;">{{ error }}</p>

        <div class="actions">
          <button class="btn btn-secondary" type="button" @click="saveDraft" :disabled="isSaving">
            Salvar rascunho
          </button>
          <button class="btn btn-primary" type="button" @click="send" :disabled="isSaving">
            Enviar
          </button>
          <button v-if="isEdit" class="btn btn-accent" type="button" @click="remove" :disabled="isSaving">
            Excluir
          </button>
          <button class="btn btn-ghost" type="button" @click="cancel" :disabled="isSaving">
            Cancelar
          </button>
        </div>
      </form>
    </div>
    <NoticeModal
      :open="noticeOpen"
      :title="noticeMessage"
      button-text="Entendi"
      @close="noticeOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { sessionStore } from '@/store/session'
import { submissionsStore } from '@/store/submissions'
import { projectsStore } from '@/store/projects'
import { accountsStore } from '@/store/accounts'
import { getTodayLocalISODate } from '@/utils/date'
import NoticeModal from '@/components/NoticeModal.vue'
import { uiStore } from '@/store/ui'

export default defineComponent({
  name: 'SafetyDialogFormView',
  components: {
    NoticeModal
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    },
    preselectedProject: {
      type: String,
      default: ''
    }
  },
  emits: ['done', 'cancel'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const attraction = ref('')
    const theme = ref('')
    const reportDate = ref(getTodayLocalISODate())
    const participants = ref<string[]>([])
    const legacyParticipants = ref<string[]>([])
    const selectedParticipant = ref('')
    const showLegacyMethod = ref(false)
    const error = ref('')
    const isSaving = ref(false)
    const noticeOpen = ref(false)
    const noticeMessage = ref('')

    const projectOptions = computed(() => {
      return projectsStore.all().map((item) => item.name).filter(Boolean)
    })

    const participantOptions = computed(() => {
      const currentUser = sessionStore.state.user
      const subordinateUsernames = Array.isArray(currentUser?.subordinateUsernames) ? currentUser?.subordinateUsernames || [] : []
      const selectedProject = projectsStore.all().find((item) => item.name === attraction.value)
      const relatedProjectUsers = selectedProject?.responsibleUsernames || []
      const usernames = Array.from(new Set([...subordinateUsernames, ...relatedProjectUsers]))
      return usernames
        .map((username) => {
          const acc = accountsStore.all().find((item) => item.username === username)
          return {
            username,
            name: acc?.name || username
          }
        })
        .filter((item) => Boolean(item.username))
    })

    const ensureProjectSelected = () => {
      if (isEdit.value) return
      if (props.preselectedProject && projectOptions.value.includes(props.preselectedProject)) {
        attraction.value = props.preselectedProject
        return
      }
      if (attraction.value) return
      if (projectOptions.value.length > 0) {
        attraction.value = projectOptions.value[0]
      }
    }

    const addSelectedParticipant = () => {
      const username = selectedParticipant.value.trim()
      if (!username) return
      if (participants.value.includes(username)) return
      participants.value.push(username)
      selectedParticipant.value = ''
    }
    const removeParticipant = (index: number) => participants.value.splice(index, 1)
    const addLegacyParticipant = () => legacyParticipants.value.push('')
    const removeLegacyParticipant = (index: number) => legacyParticipants.value.splice(index, 1)

    const isEdit = ref(false)

    const loadSubmission = () => {
      const id = (props.submissionId || (route.params.id as string)) as string | undefined
      if (!id) return
      const item = submissionsStore.getById(id)
      if (!item) return
      isEdit.value = true
      attraction.value = item.attraction
      reportDate.value = item.reportDate
      theme.value = item.observations?.[0] || ''
      participants.value = [...(item.collaborators || [])]
      legacyParticipants.value = []
    }

    const participantLabel = (value: string) => {
      const option = participantOptions.value.find((item) => item.username === value)
      return option ? `${option.name} (${option.username})` : value
    }

    const persist = async (status: 'rascunho' | 'enviado') => {
      if (isSaving.value) return
      error.value = ''
      if (!theme.value.trim()) {
        error.value = 'Informe o tema do dialogo.'
        return
      }
      if (!attraction.value) {
        error.value = 'Selecione um projeto.'
        return
      }
      const user = sessionStore.state.user
      if (!user) {
        router.push('/login')
        return
      }
      const payload = {
        title: 'Dialogos de seguranca',
        category: 'dialogo' as const,
        attraction: attraction.value,
        reportDate: reportDate.value,
        collaborators: Array.from(
          new Set([...participants.value, ...legacyParticipants.value].map((p) => p.trim()).filter(Boolean))
        ),
        tools: [],
        activities: [],
        observations: [theme.value.trim()],
        reportLanguage: 'PT' as const,
        status
      }
      try {
        if (status === 'enviado') {
          isSaving.value = true
        }
      const currentId = (props.submissionId || (route.params.id as string)) as string | undefined
      if (isEdit.value && currentId) {
        await submissionsStore.updateSubmission(currentId, payload)
      } else {
        await submissionsStore.addSubmission({
          ...payload,
          author: user
        })
      }
      if (props.embedded) {
        emit('done')
      } else {
        router.push('/home')
      }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Falha ao salvar.'
        if (message.includes('Sem permissao para enviar submissao neste projeto nesta data')) {
          noticeMessage.value = 'Nao foi possivel enviar: voce nao e responsavel pelo projeto selecionado.'
          noticeOpen.value = true
          return
        }
        error.value = message
      } finally {
        isSaving.value = false
      }
    }

    const saveDraft = () => persist('rascunho')
    const send = () => persist('enviado')
    const cancel = () => {
      if (props.embedded) {
        emit('cancel')
      } else {
        navigationStore.goBack(router, '/safety')
      }
    }

    const remove = async () => {
      const currentId = (props.submissionId || (route.params.id as string)) as string | undefined
      if (!currentId) return
      if (!await uiStore.confirm('Excluir esta submissao?')) return
      await submissionsStore.deleteSubmission(currentId)
      if (props.embedded) {
        emit('done')
      } else {
        router.push('/home')
      }
    }

    onMounted(async () => {
      await Promise.all([
        projectsStore.refresh().catch(() => {
          // ignore for now
        }),
        accountsStore.refresh().catch(() => {
          // ignore for now
        })
      ])
      await submissionsStore.refresh().catch(() => {
        // ignore for now
      })
      ensureProjectSelected()
      loadSubmission()
    })

    return {
      attraction,
      theme,
      reportDate,
      participants,
      legacyParticipants,
      selectedParticipant,
      showLegacyMethod,
      participantOptions,
      participantLabel,
      error,
      isSaving,
      noticeOpen,
      noticeMessage,
      projectOptions,
      addSelectedParticipant,
      removeParticipant,
      addLegacyParticipant,
      removeLegacyParticipant,
      saveDraft,
      send,
      cancel,
      isEdit,
      remove
    }
  }
})
</script>

<style scoped lang="scss">
.page.embedded {
  min-height: auto;
  padding: 18px 16px 28px;
  align-items: flex-start;
}

.page.embedded .card {
  max-width: none !important;
  width: 100%;
  padding: 16px;
  box-shadow: none;
  background: transparent;
  border-radius: 0;
}

@media (max-width: 760px) {
  .page.embedded {
    padding: 14px 12px 22px;
  }

  .page.embedded .card {
    padding: 12px;
  }
}

.saving-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.45);
  display: grid;
  place-items: center;
  z-index: 999;
}

.saving-card {
  background: #fff;
  padding: 18px 26px;
  border-radius: 12px;
  font-weight: 700;
}
</style>
